<script>
import { useAlert } from 'dashboard/composables';
import campaignMixin from 'shared/mixins/campaignMixin';
import MetricCard from 'dashboard/routes/dashboard/settings/reports/components/overview/MetricCard.vue';
import CampaignReportChart from '../reports/CampaignReportChart.vue';
import CampaignReportDetails from '../reports/CampaignReportDetails.vue';
import CampaignReportBenchmarks from '../reports/CampaignReportBenchmarks.vue';
import CampaignReportMessages from '../reports/CampaignReportMessages.vue';
import CampaignReportProgressBar from '../reports/CampaignReportProgressBar.vue';
import Spinner from 'shared/components/Spinner.vue';
import { getChangesOnTheme } from 'dashboard/helper/themeHelper';
import CampaignReportExtendedBenchmarks from '../reports/CampaignReportExtendedBenchmarks.vue';
import CampaignReportDeliveryDetails from '../reports/CampaignReportDeliveryDetails.vue';
import CampaignReportBaseQuality from '../reports/CampaignReportBaseQuality.vue';
import CampaignReportTemplatePreview from '../reports/CampaignReportTemplatePreview.vue';

export default {
  components: {
    MetricCard,
    CampaignReportChart,
    CampaignReportDetails,
    CampaignReportBenchmarks,
    CampaignReportMessages,
    CampaignReportProgressBar,
    Spinner,
    CampaignReportExtendedBenchmarks,
    CampaignReportDeliveryDetails,
    CampaignReportBaseQuality,
    CampaignReportTemplatePreview,
  },
  mixins: [campaignMixin],
  data() {
    return {
      isLoading: true,
      isLoadingReport: true,
      isLoadingReportMessages: true,
      campaignReport: {},
      pageIndex: 1,
      statusFilter: '',
      contactInfoFilter: '',
      defaultTextColor: '',
      currentDisplayTheme: '',
      campaign: {},
    };
  },
  computed: {
    campaignInbox() {
      return this.campaign?.inbox || {};
    },
    shouldShowErrorMessage() {
      return !this.campaign;
    },
  },
  watch: {
    campaignDisplayId() {
      if (this.$route?.params?.campaignDisplayId) {
        this.fetchCurrentCampaign();
        this.fetchCurrentCampaignReport();
      }
    },
  },
  created() {
    this.setDefaultTextColor();
  },
  mounted() {
    this.fetchCurrentCampaign();
    this.fetchCurrentCampaignReport();
    getChangesOnTheme(this.currentDisplayTheme, this);
  },
  methods: {
    async fetchCurrentCampaign() {
      const campaign = await this.$store.dispatch('campaigns/getCampaignById', {
        id: this.campaignDisplayId,
      });
      this.campaign = campaign || {};
      this.isLoading = false;
    },
    async fetchCurrentCampaignReport() {
      const reqParams = {
        id: this.campaignDisplayId,
        pageIndex: this.pageIndex,
        contactInfo: this.contactInfoFilter,
        status: this.statusFilter,
      };
      const response = await this.$store.dispatch(
        'campaigns/getReport',
        reqParams
      );
      this.isLoadingReport = false;
      this.isLoadingReportMessages = false;
      if (response === 'API error') return;
      this.campaignReport = response;
    },
    handleThemeChange(newTheme) {
      this.currentDisplayTheme = newTheme;
      this.setDefaultTextColor();
    },
    setDefaultTextColor() {
      if (!this.currentDisplayTheme)
        this.currentDisplayTheme = window.localStorage.color_scheme;

      this.defaultTextColor =
        this.currentDisplayTheme === 'dark'
          ? 'hsl(208 11.7% 91.1% / var(--tw-text-opacity))'
          : 'hsl(198 6.6% 15.8% / var(--tw-text-opacity))';
    },
    pageChange(newVal) {
      this.pageIndex = newVal;
      this.isLoadingReportMessages = true;
      this.fetchCurrentCampaignReport();
    },
    statusChange(newVal) {
      this.statusFilter = newVal;
      this.isLoadingReportMessages = true;
      this.fetchCurrentCampaignReport();
    },
    searchByContactInfo(newVal) {
      this.contactInfoFilter = newVal;
      this.isLoadingReportMessages = true;
      this.fetchCurrentCampaignReport();
    },
    cleanSearchFilter() {
      this.statusFilter = '';
      this.contactInfoFilter = '';
      this.isLoadingReportMessages = true;
      this.fetchCurrentCampaignReport();
    },
    startProgress() {
      this.$refs.progressBar.start();
    },
    async exportReportMessages(payload) {
      try {
        await this.$store.dispatch('campaigns/exportReport', {
          ...payload,
          id: this.campaignDisplayId,
        });
        useAlert(this.$t('CAMPAIGN.ONE_OFF.EXPORT_REPORT.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(
          error.message ||
            this.$t('CAMPAIGN.ONE_OFF.EXPORT_REPORT.SUCCESS_MESSAGE')
        );
      }
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto p-4">
    <div v-if="isLoading">
      <div class="items-center flex text-base justify-center">
        <Spinner color-scheme="primary" />
        <span>{{ $t('CAMPAIGN.LIST.LOADING_MESSAGE') }}</span>
      </div>
      <div class="h-[80vh]" />
    </div>
    <div v-else>
      <!-- ERROR IN API -->
      <span
        v-if="shouldShowErrorMessage"
        class="text-red-400 dark:text-red-500 text-sm block font-normal -mt-px w-full"
      >
        {{ $t('CAMPAIGN.REPORT.ERROR') }}
      </span>
      <div v-else class="flex justify-between">
        <!-- Campaign Details, Base Quality, and Template Preview -->
        <div class="column flex-1 max-height-card-container">
          <MetricCard :show-live-label="false" class="max-height-card">
            <CampaignReportDetails
              :inbox="campaignInbox"
              :campaign="campaign"
              :campaign-report="campaignReport"
            />
          </MetricCard>
        </div>
        <div class="column flex-1 max-height-card-container">
          <MetricCard :show-live-label="false" class="max-height-card">
            <CampaignReportBaseQuality
              :inbox="campaignInbox"
              :campaign="campaign"
              :campaign-report="campaignReport"
            />
          </MetricCard>
        </div>
        <div class="column flex-1 max-height-card-container">
          <MetricCard
            :show-live-label="false"
            class="max-height-card template-preview"
          >
            <CampaignReportTemplatePreview
              :inbox="campaignInbox"
              :campaign="campaign"
              :campaign-report="campaignReport"
            />
          </MetricCard>
        </div>
      </div>

      <div class="flex row justify-center mt-1">
        <MetricCard :show-live-label="false" min-height="min-h-[50px]">
          <CampaignReportProgressBar
            ref="progressBar"
            :campaign-report="campaignReport"
          />
        </MetricCard>
      </div>
      <!-- EXTENDED BENCHMARKS -->
      <div class="flex">
        <CampaignReportExtendedBenchmarks
          :is-loading="isLoadingReport"
          :campaign="campaign"
          :campaign-report="campaignReport"
          :current-display-theme="currentDisplayTheme"
        />
      </div>
      <!-- DELIVERY DETAILS -->
      <div class="flex justify-center">
        <MetricCard class="h-[200px] w-full" :show-live-label="false">
          <div
            v-if="isLoadingReport"
            class="items-center flex text-base justify-center"
          >
            <Spinner color-scheme="primary" />
          </div>
          <CampaignReportDeliveryDetails
            v-else
            :is-loading="isLoadingReport"
            :campaign="campaign"
            :campaign-report="campaignReport"
            :default-text-color="defaultTextColor"
            :current-display-theme="currentDisplayTheme"
          />
        </MetricCard>
      </div>
      <div class="responsive-benchmark">
        <!-- FUNIL DE CONVERSÃO -->
        <MetricCard class="h-[500px]" :show-live-label="false">
          <div
            v-if="isLoadingReport"
            class="items-center flex text-base justify-center"
          >
            <Spinner color-scheme="primary" />
          </div>
          <CampaignReportChart
            v-else
            :campaign-report="campaignReport"
            :default-text-color="defaultTextColor"
            :current-display-theme="currentDisplayTheme"
          />
        </MetricCard>
        <!-- BENCHMARKS -->
        <div class="max-w-[23.5rem]">
          <CampaignReportBenchmarks
            :is-loading="isLoadingReport"
            :campaign="campaign"
            :campaign-report="campaignReport"
            :current-display-theme="currentDisplayTheme"
          />
        </div>
      </div>
      <!-- MESSAGES CONTAINER -->
      <div class="flex row">
        <MetricCard :show-live-label="false" class="custom-max-width">
          <CampaignReportMessages
            :campaign-report="campaignReport"
            :page-index="pageIndex"
            :is-loading="isLoadingReportMessages"
            @page-change="pageChange"
            @status-change="statusChange"
            @search-by-contactinfo="searchByContactInfo"
            @clean-filter="cleanSearchFilter"
            @export-report-messages="exportReportMessages"
          />
        </MetricCard>
      </div>
    </div>
  </div>
</template>

<style scoped>
.column {
  flex: 1;
}
.custom-max-width {
  max-width: calc(100vw - 100px);
}

.max-height-card-container {
  max-height: 300px; /* Ajuste o valor conforme necessário */
}

.max-height-card {
  height: 100%;
}

.template-preview {
  overflow: auto;
}

@media (min-width: 1236px) {
  .responsive-benchmark {
    display: flex;
  }
}

@media (max-width: 1236px) {
  .flex {
    flex-wrap: wrap;
  }
}
</style>
